'use client';

/**
 * The Compare section of the Ads admin tab (ads build plan section 5.3): per
 * source and placement, over 7 or 30 days, the requests, fills, wins,
 * impressions, viewables, clicks, estimated revenue, eCPM, CTR, p50 / p95
 * latency, timeouts, errors, and rejects and skips by reason, from
 * `GET /api/admin/ads/compare`.
 *
 * The lane picker keeps the three kinds of traffic apart: all real traffic
 * (mediated plus holdout), the holdout alone (one source per request, the
 * clean standalone numbers), and test mode (fixture answers).
 *
 * Revenue is ESTIMATED from the prices the sources returned (a CPM ad earns on
 * its impression, a CPC ad on its click, an unknown-priced ad on neither);
 * nightly reconciliation against the sources' own reports is not built.
 *
 * READABLE IN THE ADMIN DIALOG'S COLUMN (the 2026-09-30 fix round: the visual
 * pass found "p50 / p95 ms" wrapped onto four lines and the table scrolling
 * sideways, with Rejects and Skipped off-screen). Headers are short and never
 * wrap, each with its full name in `title`; the row's name (source over
 * placement) is one sticky first column, so a sideways scroll never loses
 * which row a number belongs to; every number is right-aligned in tabular
 * figures and never wraps; the fill rate sits under the fill count; and the
 * two reason lists get a floor width and wrap inside it.
 *
 * ONE MEANING PER NUMBER (fix round 2, 2026-09-30; components/admin/
 * ads-metrics.ts): CTR is clicks over impressions here and in the house ads
 * table, under the same label and tooltip, and a row with nothing to time (a
 * house row: picked on the platform, no network call) reads "n/a" with the
 * reason, where it used to be a bare "/".
 */

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { CTR_LABEL, CTR_TITLE, ctrText, latencyCell } from '@/components/admin/ads-metrics';
import { cn } from '@/lib/utils';

interface CompareRow {
  source: string;
  placement: string;
  requests: number;
  fills: number;
  wins: number;
  impressions: number;
  viewables: number;
  clicks: number;
  timeouts: number;
  errors: number;
  revenueUsd: number;
  ecpm: number;
  ctr: number;
  fillRate: number;
  p50Ms: number | null;
  p95Ms: number | null;
  rejects: Record<string, number>;
  skips: Record<string, number>;
  /** Wins that answered a refresh (the refresh round, 2026-09-30); absent from an older pod's rows. */
  refreshes?: number;
}

const LANES = [
  { id: 'live', label: 'All traffic' },
  { id: 'holdout', label: 'Holdout only' },
  { id: 'test', label: 'Test mode' },
] as const;

const buttonClass = 'inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50';

/** The table's columns after the sticky name: a short header, and the full name for its `title`. */
export const COMPARE_COLUMNS = [
  { key: 'requests', label: 'Req', title: 'Requests: times the source was asked', numeric: true },
  { key: 'fills', label: 'Fills', title: 'Fills: asked and answered with an ad that passed validation (fill rate under the count)', numeric: true },
  { key: 'wins', label: 'Wins', title: 'Wins: its ad won the mediation and was served', numeric: true },
  { key: 'refreshes', label: 'Refr', title: 'Refreshes: wins that answered a refresh (a timed rotation, or a navigation after the ad was in view), a part of the wins', numeric: true },
  { key: 'impressions', label: 'Impr', title: 'Impressions', numeric: true },
  { key: 'viewables', label: 'View', title: 'Viewable impressions', numeric: true },
  { key: 'clicks', label: 'Clicks', title: 'Clicks, counted at our redirect', numeric: true },
  { key: 'ctr', label: CTR_LABEL, title: CTR_TITLE, numeric: true },
  { key: 'revenue', label: 'Rev', title: 'Estimated revenue in USD, from the prices the sources returned', numeric: true },
  { key: 'ecpm', label: 'eCPM', title: 'Estimated revenue per 1000 impressions, in USD', numeric: true },
  { key: 'latency', label: 'p50/p95', title: 'Latency in ms, median / 95th percentile (upper bounds of the latency buckets)', numeric: true },
  { key: 'timeouts', label: 'T/O', title: 'Timeouts', numeric: true },
  { key: 'errors', label: 'Err', title: 'Errors', numeric: true },
  { key: 'rejects', label: 'Rejects', title: 'Answers refused by validation, by reason', numeric: false },
  { key: 'skips', label: 'Skipped', title: 'Times the source was not asked, by reason', numeric: false },
] as const;

/** The sticky first column: opaque (the rest scrolls under it) with a hairline on its right. */
const STICKY_CELL = 'sticky left-0 z-[1] shadow-[inset_-1px_0_0_var(--color-border)]';
/** The header row's `bg-muted/40` made opaque over the page, for the sticky header cell. */
const STICKY_HEAD_BG = 'bg-[color:color-mix(in_oklab,var(--color-muted)_40%,var(--color-background))]';
const NUM_CELL = 'px-2 py-1.5 text-right tabular-nums whitespace-nowrap';
const REASON_CELL = 'min-w-[7rem] px-2 py-1.5 text-[11px] leading-snug text-muted-foreground';

function reasons(map: Record<string, number>): string {
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${k} ${n}`)
    .join(', ');
}

export function AdsComparePanel({ refreshKey }: { refreshKey: number }) {
  const [days, setDays] = useState<7 | 30>(7);
  const [lane, setLane] = useState<(typeof LANES)[number]['id']>('live');
  const [rows, setRows] = useState<CompareRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/ads/compare?days=${days}&lane=${lane}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const body = (await res.json()) as { rows?: CompareRow[] };
      setRows(body.rows ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [days, lane]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <section className="rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Compare sources</h2>
        <div className="flex items-center gap-1 text-xs">
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={cn('rounded-md border px-2 py-1', days === d ? 'border-foreground bg-foreground text-background' : 'border-border hover:bg-muted')}
            >
              {d} days
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 text-xs">
          {LANES.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setLane(l.id)}
              className={cn('rounded-md border px-2 py-1', lane === l.id ? 'border-foreground bg-foreground text-background' : 'border-border hover:bg-muted')}
            >
              {l.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => void load()} disabled={loading} className={cn(buttonClass, 'ml-auto')}>
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>
      {error ? <p className="px-4 py-3 text-sm text-destructive">{error}</p> : null}
      <div className="overflow-x-auto" data-testid="ads-compare-scroll">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-left text-[11px] text-muted-foreground">
            <tr>
              <th
                scope="col"
                title="Source and placement"
                className={cn(STICKY_CELL, STICKY_HEAD_BG, 'whitespace-nowrap px-2 py-1.5 font-medium')}
              >
                Source
              </th>
              {COMPARE_COLUMNS.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  title={c.title}
                  className={cn('whitespace-nowrap px-2 py-1.5 font-medium', c.numeric ? 'text-right' : 'text-left')}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COMPARE_COLUMNS.length + 1} className="px-3 py-6 text-center text-muted-foreground">
                  {loading ? 'Loading…' : 'Nothing counted in this window.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const latency = latencyCell(r);
                return (
                  <tr key={`${r.source}|${r.placement}`} className="border-t border-border">
                    <th scope="row" className={cn(STICKY_CELL, 'bg-background whitespace-nowrap px-2 py-1.5 text-left font-normal')}>
                      <span className="block font-medium">{r.source}</span>
                      <span className="block text-[11px] text-muted-foreground">{r.placement}</span>
                    </th>
                    <td className={NUM_CELL}>{r.requests}</td>
                    <td className={NUM_CELL}>
                      <span className="block">{r.fills}</span>
                      {r.requests > 0 ? (
                        <span className="block text-[11px] text-muted-foreground">{(r.fillRate * 100).toFixed(0)}%</span>
                      ) : null}
                    </td>
                    <td className={NUM_CELL}>{r.wins}</td>
                    <td className={NUM_CELL}>{r.refreshes ?? 0}</td>
                    <td className={NUM_CELL}>{r.impressions}</td>
                    <td className={NUM_CELL}>{r.viewables}</td>
                    <td className={NUM_CELL}>{r.clicks}</td>
                    <td className={NUM_CELL}>{ctrText(r.clicks, r.impressions)}</td>
                    <td className={NUM_CELL}>${r.revenueUsd.toFixed(2)}</td>
                    <td className={NUM_CELL}>${r.ecpm.toFixed(2)}</td>
                    <td className={NUM_CELL} title={latency.title}>
                      {latency.text}
                    </td>
                    <td className={NUM_CELL}>{r.timeouts}</td>
                    <td className={NUM_CELL}>{r.errors}</td>
                    <td className={REASON_CELL}>{reasons(r.rejects)}</td>
                    <td className={REASON_CELL}>{reasons(r.skips)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
